import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, ChevronLeft, ChevronRight, Trash2 } from "lucide-react";
import { toast } from "sonner";

const TYPE_COLORS = {
  appointment: "#C87B67",
  medication: "#3A5A4A",
  task: "#7A736E",
  important: "#D9A441",
  general: "#9CA88F",
};

const EMPTY_FORM = { title: "", type: "general", date: "", time: "", recurring: "none", notes: "" };

export default function HopePlanner() {
  const { chestId } = useParams();
  const [events, setEvents] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [today] = useState(new Date());
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  // Edit state — if editing, holds {id, src ("event"|"appt"), title?, doctor?, specialty?, location?}
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const monthStartIso = new Date(cursor.getFullYear(), cursor.getMonth(), 1).toISOString().slice(0, 10);
      const monthEndIso = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).toISOString().slice(0, 10);
      const [evRes, apRes] = await Promise.all([
        api.get(`/hope-chests/${chestId}/planner-events-expanded?start=${monthStartIso}&end=${monthEndIso}`),
        api.get(`/hope-chests/${chestId}/appointments`),
      ]);
      setEvents(evRes.data); setAppointments(apRes.data);
    } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [chestId, cursor]);

  const ymd = (d) => d.toISOString().slice(0, 10);

  // Merge planner events + appointments for unified view
  const merged = [
    ...events.map((e) => ({ ...e, src: "event", _date: e._occurrence_date || e.date, _type: e.type || "general" })),
    ...appointments.map((a) => ({
      ...a, src: "appt", title: `${a.doctor}${a.specialty ? " • " + a.specialty : ""}`,
      _date: (a.date || "").slice(0, 10), _time: (a.date || "").slice(11, 16), _type: "appointment",
    })),
  ];

  const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0);
  const startWeekday = monthStart.getDay();
  const daysInMonth = monthEnd.getDate();
  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));

  const eventsForDay = (d) => merged.filter((e) => e._date === ymd(d));

  const openEdit = (item) => {
    if (item.src === "event") {
      setEditing({
        id: item.id,
        src: "event",
      });
      setForm({
        title: item.title || "",
        type: item.type || "general",
        date: item._date || item.date || "",
        time: item.time || "",
        recurring: item.recurring || "none",
        notes: item.notes || "",
      });
    } else {
      // Appointment — different schema (doctor, specialty, location, date as full ISO)
      setEditing({
        id: item.id,
        src: "appt",
      });
      const dateOnly = (item.date || "").slice(0, 10);
      const timeOnly = (item.date || "").slice(11, 16);
      setForm({
        title: "",  // not used for appointment edit
        doctor: item.doctor || "",
        specialty: item.specialty || "",
        location: item.location || "",
        date: dateOnly,
        time: timeOnly,
        notes: item.notes || "",
        type: "appointment",
        recurring: "none",
      });
    }
    setDialogOpen(true);
  };

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const submit = async () => {
    if (saving) return;
    if (editing?.src === "appt") {
      if (!form.doctor?.trim() || !form.date) { toast.error("Doctor and date are required"); return; }
    } else {
      if (!form.title.trim() || !form.date) { toast.error("Title and date are required"); return; }
    }
    setSaving(true);
    try {
      if (editing?.src === "event") {
        await api.put(`/hope-chests/${chestId}/planner-events/${editing.id}`, {
          title: form.title, type: form.type, date: form.date, time: form.time || null,
          recurring: form.recurring, notes: form.notes,
        });
        toast.success("Event updated");
      } else if (editing?.src === "appt") {
        // Combine date + time → ISO. If no time, use 09:00
        const isoDate = `${form.date}T${form.time || "09:00"}:00`;
        await api.put(`/hope-chests/${chestId}/appointments/${editing.id}`, {
          doctor: form.doctor, specialty: form.specialty || null, location: form.location || null,
          date: isoDate, notes: form.notes || null,
        });
        toast.success("Appointment updated");
      } else {
        await api.post(`/hope-chests/${chestId}/planner-events`, form);
        toast.success("Added to Hope Planner");
      }
      setDialogOpen(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!editing) return;
    if (!window.confirm(editing.src === "appt"
      ? "Delete this appointment? This can't be undone."
      : "Delete this planner event? If it repeats, all future occurrences will be removed.")) return;
    setSaving(true);
    try {
      if (editing.src === "event") {
        await api.delete(`/hope-chests/${chestId}/planner-events/${editing.id}`);
      } else {
        await api.delete(`/hope-chests/${chestId}/appointments/${editing.id}`);
      }
      toast.success("Deleted");
      setDialogOpen(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't delete");
    } finally {
      setSaving(false);
    }
  };

  const monthLabel = cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  const upcoming = merged
    .filter((e) => e._date >= ymd(today))
    .sort((a, b) => (a._date + (a._time || "")).localeCompare(b._date + (b._time || "")))
    .slice(0, 6);

  const isAppt = editing?.src === "appt";
  const isEdit = !!editing;

  return (
    <AppShell chestId={chestId}>
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#7A736E]">Hope Planner</p>
            <h1 className="font-display text-3xl md:text-4xl font-semibold mt-1">{monthLabel}</h1>
            <p className="text-xs text-[#7A736E] mt-1">Tip: click any event to edit or delete it.</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) { setEditing(null); setForm(EMPTY_FORM); } }}>
            <DialogTrigger asChild>
              <Button onClick={openNew} className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538]" data-testid="planner-add-button">
                <Plus className="w-4 h-4 mr-2" /> New event
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-2xl max-w-md">
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">
                  {isEdit ? (isAppt ? "Edit appointment" : "Edit planner event") : "Add to Hope Planner"}
                </DialogTitle>
                <DialogDescription>
                  {isAppt
                    ? "Update the doctor, time, or location for this appointment."
                    : "Schedule appointments, medications, tasks, or important dates."}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                {isAppt ? (
                  <>
                    <div><Label>Doctor / clinic *</Label><Input value={form.doctor || ""} onChange={(e) => setForm({ ...form, doctor: e.target.value })} className="rounded-xl mt-1.5" data-testid="planner-doctor" /></div>
                    <div><Label>Specialty</Label><Input value={form.specialty || ""} onChange={(e) => setForm({ ...form, specialty: e.target.value })} className="rounded-xl mt-1.5" placeholder="Cardiology, dermatology, etc." /></div>
                    <div><Label>Location</Label><Input value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} className="rounded-xl mt-1.5" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Date *</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-xl mt-1.5" data-testid="planner-date" /></div>
                      <div><Label>Time</Label><Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="rounded-xl mt-1.5" data-testid="planner-time" /></div>
                    </div>
                    <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-xl mt-1.5" /></div>
                  </>
                ) : (
                  <>
                    <div><Label>Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl mt-1.5" data-testid="planner-title" /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Type</Label>
                        <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full mt-1.5 rounded-xl border border-[#EBE5DF] px-3 py-2 bg-white" data-testid="planner-type">
                          {["general", "appointment", "medication", "task", "important"].map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <div><Label>Date *</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-xl mt-1.5" data-testid="planner-date" /></div>
                    </div>
                    <div><Label>Time</Label><Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} className="rounded-xl mt-1.5" data-testid="planner-time" /></div>
                    {!isEdit && (
                      <div>
                        <Label>Repeats</Label>
                        <select value={form.recurring} onChange={(e) => setForm({ ...form, recurring: e.target.value })} className="w-full mt-1.5 rounded-xl border border-[#EBE5DF] px-3 py-2 bg-white" data-testid="planner-recurring">
                          <option value="none">Doesn't repeat</option>
                          <option value="daily">Every day</option>
                          <option value="weekly">Every week</option>
                          <option value="monthly">Every month</option>
                        </select>
                      </div>
                    )}
                    <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-xl mt-1.5" /></div>
                  </>
                )}
              </div>
              <DialogFooter className="gap-2 sm:gap-0 sm:justify-between">
                {isEdit ? (
                  <Button onClick={remove} disabled={saving} variant="outline" className="rounded-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700" data-testid="planner-delete">
                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                  </Button>
                ) : <span />}
                <Button onClick={submit} disabled={saving} className="rounded-full bg-[#3A5A4A] hover:bg-[#2C4538]" data-testid="planner-submit">
                  {saving ? "Saving…" : (isEdit ? "Save changes" : "Save event")}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 hc-card p-6">
            <div className="flex items-center justify-between mb-4">
              <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="p-2 rounded-full hover:bg-[#F3EFEA]" data-testid="planner-prev"><ChevronLeft className="w-4 h-4" /></button>
              <span className="font-display text-lg">{monthLabel}</span>
              <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="p-2 rounded-full hover:bg-[#F3EFEA]" data-testid="planner-next"><ChevronRight className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-7 text-xs uppercase tracking-wider text-[#7A736E] mb-2">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="p-2 text-center">{d}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((d, i) => (
                <div key={i} className={`aspect-square rounded-xl p-1.5 text-xs ${d ? "bg-[#FCFAF8] hover:bg-[#F3EFEA]" : ""} ${d && ymd(d) === ymd(today) ? "ring-2 ring-[#3A5A4A]" : ""}`}>
                  {d && (
                    <>
                      <div className="font-medium">{d.getDate()}</div>
                      <div className="space-y-0.5 mt-1">
                        {eventsForDay(d).slice(0, 2).map((e, idx) => (
                          <button
                            key={idx}
                            onClick={() => openEdit(e)}
                            className="text-[10px] truncate px-1 py-0.5 rounded text-white block w-full text-left hover:opacity-80 transition cursor-pointer"
                            style={{ background: TYPE_COLORS[e._type] || "#3A5A4A" }}
                            title={`${e.title} — click to edit`}
                            data-testid={`planner-event-${e.src}-${e.id}`}
                          >
                            {e.title}
                          </button>
                        ))}
                        {eventsForDay(d).length > 2 && <div className="text-[10px] text-[#7A736E]">+{eventsForDay(d).length - 2}</div>}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div className="hc-card p-6">
            <h3 className="font-display text-lg mb-4">Upcoming</h3>
            <div className="space-y-3">
              {upcoming.length === 0 && <p className="text-sm text-[#7A736E]">Nothing planned yet.</p>}
              {upcoming.map((e, i) => (
                <button
                  key={i}
                  onClick={() => openEdit(e)}
                  className="flex gap-3 items-start w-full text-left p-2 -mx-2 rounded-lg hover:bg-[#F3EFEA] transition"
                  data-testid={`upcoming-${i}`}
                  title="Click to edit"
                >
                  <div className="w-2 h-2 rounded-full mt-2" style={{ background: TYPE_COLORS[e._type] || "#3A5A4A" }} />
                  <div className="flex-1">
                    <p className="text-sm font-medium">{e.title}</p>
                    <p className="text-xs text-[#7A736E]">{e._date} {e._time && `• ${e._time}`}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
